import React from 'react';

/**
 * ApprovalTrail - Displays the approval chain audit trail
 * Based on approval.md UI/UX specifications
 */
const ApprovalTrail = ({ approvalChain, integrityStatus }) => {
  if (!approvalChain) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center text-gray-500">
          <p>Loading approval history...</p>
        </div>
      </div>
    );
  }

  const { trail = [], currentStatus, version, integrityErrors = [] } = approvalChain;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStageIcon = (stage, isCompleted) => {
    if (stage === 'rejected') return '✕';
    if (stage === 'submitted') return isCompleted ? '✓' : '○';
    if (stage === 'auditor_approved') return isCompleted ? '✓' : '○';
    if (stage === 'regional_director_approved') return isCompleted ? '✓✓' : '○';
    if (stage === 'global_director_approved') return isCompleted ? '✓✓✓' : '○';
    return isCompleted ? '✓' : '○';
  };

  const getStageColor = (stage, isCompleted) => {
    if (stage === 'rejected') return 'border-red-500 bg-red-50';
    if (!isCompleted) return 'border-gray-300 bg-gray-50';
    return 'border-green-500 bg-green-50';
  };

  const getConnectorColor = (isCompleted) => {
    return isCompleted ? 'bg-green-500' : 'bg-gray-300';
  };

  // Define the full approval stages
  const allStages = [
    { key: 'submitted', label: 'Submitted to Auditor for Review' },
    { key: 'auditor_approved', label: 'Auditor Approved' },
    { key: 'regional_director_approved', label: 'Regional Director Approved' },
    { key: 'global_director_approved', label: 'Global Director Approved' }
  ];

  // Build the display trail with pending stages
  const getDisplayTrail = () => {
    const completedStages = new Set(trail.map(t => t.stage));
    const displayItems = [];

    // Add completed stages from trail
    trail.forEach((item) => {
      displayItems.push({
        ...item,
        isCompleted: true,
        isPending: false
      });
    });

    // If not rejected, add pending stages
    if (currentStatus !== 'rejected' && currentStatus !== 'global_director_approved') {
      const currentIndex = allStages.findIndex(s => s.key === currentStatus);
      for (let i = currentIndex + 1; i < allStages.length; i++) {
        if (!completedStages.has(allStages[i].key)) {
          displayItems.push({
            stage: allStages[i].key,
            label: `Awaiting ${allStages[i].label.replace(' Approved', '')}`,
            isCompleted: false,
            isPending: true
          });
        }
      }
    }

    return displayItems;
  };

  const displayTrail = getDisplayTrail();

  // approval-trail-* classes are print hooks only (index.css: MONTHLY REPORT -
  // PRINTED DOCUMENT prints the monthly report's trail as sign-off blocks).
  return (
    <div className="approval-trail bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="approval-trail-head bg-gray-800 text-white px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold">Approval History</h3>
          <p className="text-gray-300 text-sm">Version {version || 1}</p>
        </div>
        {/* Integrity Badge */}
        {integrityStatus && (
          <div className={`approval-trail-integrity flex-shrink-0 px-3 py-1 rounded-full text-xs sm:text-sm text-center font-medium ${
            integrityStatus.valid
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
          }`}>
            {integrityStatus.valid ? 'Integrity Verified' : 'Warning: Tampering Detected'}
          </div>
        )}
      </div>

      {/* Integrity Errors */}
      {integrityErrors.length > 0 && (
        <div className="approval-trail-errors bg-red-50 border-b border-red-200 px-4 sm:px-6 py-3 break-words">
          <p className="text-red-700 font-medium text-sm">Warning: Data integrity issues detected</p>
          {integrityErrors.map((error, idx) => (
            <p key={idx} className="text-red-600 text-xs mt-1">
              Stage: {error.stage} - {error.error}
            </p>
          ))}
        </div>
      )}

      {/* Timeline */}
      <div className="approval-trail-body p-4 sm:p-6">
        <div className="approval-trail-steps relative">
          {displayTrail.map((item, index) => (
            <div key={index} className={`approval-trail-step${item.isPending ? ' approval-trail-pending' : ''} relative pb-8 last:pb-0`}>
              {/* Connector line */}
              {index < displayTrail.length - 1 && (
                <div className={`approval-trail-line absolute left-4 top-8 w-0.5 h-full -ml-px ${getConnectorColor(item.isCompleted)}`} />
              )}

              <div className="relative flex items-start gap-3 sm:gap-4">
                {/* Stage icon */}
                <div className={`approval-trail-icon flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${getStageColor(item.stage, item.isCompleted)}`}>
                  {item.isPending ? '-' : getStageIcon(item.stage, item.isCompleted)}
                </div>

                {/* Content */}
                <div className={`approval-trail-content flex-grow min-w-0 ${item.isPending ? 'opacity-50' : ''}`}>
                  <div className="approval-trail-title flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-1 sm:gap-2">
                    <span className="font-semibold text-gray-800">
                      {item.label || item.stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                    {item.verified && (
                      <span className="approval-trail-verified text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    )}
                  </div>

                  {item.by && (
                    <p className="approval-trail-by text-gray-600 text-sm mt-1">
                      by {item.by}
                      {item.byRole && (
                        <span className="approval-trail-role text-gray-400 ml-1">({item.byRole})</span>
                      )}
                    </p>
                  )}

                  {item.at && (
                    <p className="approval-trail-at text-gray-500 text-xs mt-1">
                      {formatDate(item.at)}
                    </p>
                  )}

                  {item.comments && (
                    <p className="approval-trail-comment text-gray-700 text-sm mt-2 italic bg-gray-50 px-3 py-2 rounded border-l-4 border-gray-300 break-words">
                      "{item.comments}"
                    </p>
                  )}

                  {item.isPending && (
                    <p className="approval-trail-pending-note text-gray-400 text-sm italic mt-1">(pending)</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ApprovalTrail;
